import "./galleryPanel.css";
import { Suspense, useEffect } from "react";
import { getAwacTemplate } from "../awac/templateRegistry";
import TemplateErrorBoundary from "../awac/TemplateErrorBoundary";
import { t } from "../awac/i18n";
import { localizeAgentText } from "../awac/agentTextL10n";

/**
 * GalleryPanel
 * ---------------------------------------------------------------------------
 * The gallery WING. It renders inside the same widget container as the chat,
 * as a second column to the LEFT of the chat. The chat column itself is left
 * untouched — it keeps showing the agent's intro line and the round-trip
 * option buttons. This wing only mounts when there is an active gallery
 * envelope, and can be dismissed with its close button (which collapses the
 * widget back to a single chat column) or with the Escape key.
 *
 * Templates are code-split (React.lazy) and rendered inside a Suspense
 * boundary, wrapped in a TemplateErrorBoundary so a template crash can never
 * take the conversation down with it.
 *
 * @param {object} props
 * @param {object} props.envelope - The parsed AWAC envelope to render.
 * @param {string} props.templateName - Registered template name (e.g. FloorPlanShowcase).
 * @param {Function} props.onClose - () => void. Dismisses the panel.
 * @param {Function} [props.onAction] - (action) => void. Optional. Forwarded to the
 *        template so card-level affordances (e.g. "Book a visit") can round-trip
 *        back to the chat as a normal text message.
 */
export default function GalleryPanel({ envelope, templateName, onClose, onAction }) {
    // Escape closes the gallery wing (but leaves the chat itself open).
    useEffect(() => {
        if (!envelope) {
            return undefined;
        }
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose && onClose();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [envelope, onClose]);

    if (!envelope) {
        return null;
    }

    const Template = getAwacTemplate(templateName);
    const persAttrs = (Array.isArray(envelope.curation) && envelope.curation[0] &&
        Array.isArray(envelope.curation[0].personalizations) &&
        envelope.curation[0].personalizations[0]) || {};
    // Default panel header per template so the wing reads correctly for each.
    // Titles come from the i18n tables (gallery.<TemplateName>) so the panel
    // header follows the site language.
    const KNOWN_TITLE_TEMPLATES = [
        "FloorPlanShowcase",
        "UnitCarousel",
        "NeighborhoodMap",
        "BookingFrame",
        "BookingSlots",
        "BookingConfirm"
    ];
    const defaultTitle = KNOWN_TITLE_TEMPLATES.indexOf(templateName) !== -1
        ? t(`gallery.${templateName}`)
        : t("gallery.details");
    const rawTitle = persAttrs.title ||
        (persAttrs.attributes && persAttrs.attributes.IntroductionText);
    const title = rawTitle ? localizeAgentText(rawTitle, false) : defaultTitle;

    // A new template render (different template or envelope) resets a previous
    // error state so the fresh content gets a clean mount.
    const boundaryResetKey = `${templateName}:${envelope.text || ""}`;

    return (
        <section className="awacGalleryColumn" role="complementary" aria-label={title}>
            <div className="awacGalleryPanelHeader">
                <span className="awacGalleryPanelTitle">{title}</span>
                <button
                    type="button"
                    className="awacGalleryPanelClose"
                    aria-label={t("gallery.close")}
                    title={t("gallery.close")}
                    onClick={onClose}>
                    &times;
                </button>
            </div>
            <div className="awacGalleryPanelBody">
                {Template ? (
                    <TemplateErrorBoundary resetKey={boundaryResetKey}>
                        <Suspense fallback={<div className="awacGalleryPanelLoading" aria-hidden="true" />}>
                            <Template payload={envelope} onAction={onAction} />
                        </Suspense>
                    </TemplateErrorBoundary>
                ) : (
                    <div className="awacGalleryPanelUnknown">
                        <p>{t("gallery.unknownTitle")}</p>
                        <small>{t("gallery.unknownSub")}{templateName ? `: ${templateName}` : ""}.</small>
                    </div>
                )}
            </div>
        </section>
    );
}
